export interface SanitizableSentryEvent {
  /**
   * The route name. Sentry derives it from the URL, so on a route that carries
   * a credential in the query string (`/reset-password?token=…`) the secret
   * ends up in the event title, not just in `request.url`.
   */
  transaction?: string
  request?: {
    headers?: unknown
    cookies?: unknown
    query_string?: unknown
    url?: string
    data?: unknown
    [key: string]: unknown
  }
  user?: Record<string, unknown> | null
  contexts?: Record<string, unknown>
  extra?: Record<string, unknown>
  breadcrumbs?: SanitizableSentryBreadcrumb[]
  message?: string
  exception?: {
    values?: Array<{
      type?: string
      value?: string
      [key: string]: unknown
    }>
  }
}

export interface SanitizableSentryBreadcrumb {
  message?: string
  data?: Record<string, unknown>
}

export interface SanitizableSentrySpan {
  /** HTTP spans describe themselves as `POST https://host/path?…`. */
  description?: string
  data?: Record<string, unknown>
}

export interface SanitizableSentryTransaction extends SanitizableSentryEvent {
  contexts?: Record<string, unknown> & { trace?: SanitizableSentrySpan }
  spans?: SanitizableSentrySpan[]
}

export const SENTRY_PRIVACY_DATA_COLLECTION = {
  userInfo: false,
  cookies: false,
  httpHeaders: { request: false, response: false },
  httpBodies: [],
  urlQueryParams: false,
  genAI: { inputs: false, outputs: false },
  stackFrameVariables: false,
  frameContextLines: 7,
}

const BREADCRUMB_URL_FIELDS = ['from', 'to', 'url']
const URL_ATTRIBUTE_FIELDS = new Set(['url', 'url.full', 'url.original', 'http.url', 'http.target', 'request.url'])
const SENSITIVE_SPAN_ATTRIBUTE_KEYS = new Set([
  'authorization',
  'cookie',
  'cookies',
  'query',
  'query_string',
  'request.query',
  'request.query_string',
  'request.cookies',
  'response.cookies',
  'http.request.cookie',
  'http.response.cookie',
  'url.query',
  'url.fragment',
  'http.query',
  'http.fragment',
  'http.request.header',
  'http.response.header',
  'http.request.headers',
  'http.response.headers',
  'request.headers',
  'response.headers',
  'gen_ai.input.messages',
  'gen_ai.output.messages',
  'gen_ai.prompt',
  'gen_ai.response',
  'gen_ai.completion',
  'ai.input',
  'ai.output',
  'ai.prompt',
  'ai.response',
])
const HEADER_ATTRIBUTE_PREFIXES = [
  'http.request.header.',
  'http.response.header.',
  'request.header.',
  'response.header.',
  'request.headers.',
  'response.headers.',
]

const SENTRY_REDACTED_VALUE = '[REDACTED]'

const SENSITIVE_METADATA_KEYS = new Set([
  'ssn',
  'ein',
  'itin',
  'taxid',
  'password',
  'secret',
  'token',
  'accesstoken',
  'refreshtoken',
  'idtoken',
  'apikey',
  'routingnumber',
  'accountnumber',
  'creditcard',
  'cardnumber',
  'socialsecurity',
  'email',
  'firstname',
  'lastname',
  'fullname',
  'companyname',
  'filename',
  'originalfilename',
  'filestoragepath',
  'storagepath',
  'objectkey',
  'r2key',
  'sessionid',
  'prompt',
  'input',
  'output',
  'rawerror',
  'errorresponse',
  'validationerrors',
  'query',
  'searchquery',
  'queryparams',
  'filters',
  'requestbody',
  'payload',
])

const SAFE_DIAGNOSTIC_METADATA_KEYS = new Set([
  'model',
  'modelname',
  'inputtokens',
  'outputtokens',
  'totaltokens',
  'tokencount',
  'durationms',
  'requestid',
  'operationtype',
  'attempt',
  'retrycount',
  'status',
])

const SENSITIVE_METADATA_KEY_FRAGMENTS = [
  'ssn',
  'ein',
  'itin',
  'taxid',
  'socialsecurity',
  'email',
  'phone',
  'customername',
  'companyname',
  'firstname',
  'lastname',
  'fullname',
  'originalfilename',
  'filename',
  'filepath',
  'filestoragepath',
  'documentstoragepath',
  'storagepath',
  'objectkey',
  'r2key',
  'sessionid',
  'sessionstate',
  'password',
  'secret',
  'credential',
  'accesstoken',
  'refreshtoken',
  'idtoken',
  'token',
  'apikey',
  'routingnumber',
  'accountnumber',
  'creditcard',
  'cardnumber',
  'prompt',
  'modelinput',
  'modeloutput',
  'rawerror',
  'errorresponse',
  'validationerrors',
  'queryparams',
  'searchquery',
  'requestbody',
  'responsebody',
]

const SSN_PATTERN = /\b\d{3}[-\s]?\d{2}[-\s]?\d{4}\b/g
const EIN_PATTERN = /\b\d{2}[-\s]?\d{7}\b/g
const EMAIL_PATTERN = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi
const CREDENTIAL_VALUE_PATTERN =
  /((?:access[_-]?token|api[_-]?key|credential|id[_-]?token|password|refresh[_-]?token|secret|session[_-]?state|signature|token)=)[^&\s]+/gi
// The AI SDK builds `JSONParseError.message` as
// `JSON parsing failed: Text: ${text}.\nError message: ${cause}`, where `text` is
// the model's entire output — which echoes the customer's document. Redact that
// payload while keeping the surrounding diagnostic. The alternation lets a
// truncated message (no `Error message:` tail) redact to end of string.
const AI_PARSE_OUTPUT_PATTERN = /(JSON parsing failed: Text: )[\s\S]*?(\.\r?\nError message:|$)/g

function normalizeMetadataKey(key: string): string {
  return key.replace(/[^a-z\d]/gi, '').toLowerCase()
}

function isSensitiveMetadataKey(key: string): boolean {
  const normalizedKey = normalizeMetadataKey(key)
  if (
    SAFE_DIAGNOSTIC_METADATA_KEYS.has(normalizedKey) ||
    /^(?:(?:input|output|total|cached|reasoning|prompt|completion)tokens?(?:count|used|usage)?|tokens?(?:count|used|usage))$/.test(
      normalizedKey,
    )
  ) {
    return false
  }
  return (
    SENSITIVE_METADATA_KEYS.has(normalizedKey) ||
    SENSITIVE_METADATA_KEY_FRAGMENTS.some((fragment) => normalizedKey.includes(fragment))
  )
}

export function sanitizeSentryText(value: string): string {
  return value
    .replace(AI_PARSE_OUTPUT_PATTERN, '$1[REDACTED-AI-OUTPUT]$2')
    .replace(SSN_PATTERN, '[REDACTED-SSN]')
    .replace(EIN_PATTERN, '[REDACTED-EIN]')
    .replace(EMAIL_PATTERN, '[REDACTED-EMAIL]')
    .replace(CREDENTIAL_VALUE_PATTERN, '$1[REDACTED]')
    .replace(TOKEN_PATH_PATTERN, '$1[REDACTED]')
}

export function sentryErrorType(error: unknown): string {
  if (error instanceof Error && error.name) return error.name
  if (error === null) return 'null'
  if (Array.isArray(error)) return 'Array'
  if (typeof error === 'object') return error.constructor?.name || 'Object'
  return typeof error
}

function sanitizeSentryStackFrame(line: string): string | null {
  if (/^\s*at\s+/.test(line)) {
    return sanitizeSentryText(line).replace(/https?:\/\/[^\s)]+/gi, stripSentryUrlSecrets)
  }

  const browserFrame = /^(\s*[^@\n]*@)(.+:\d+(?::\d+)?)\s*$/.exec(line)
  if (!browserFrame) return null
  // Both groups are mandatory in the pattern; the `?? ''` is unreachable and
  // exists only for `noUncheckedIndexedAccess`, which the Worker tsconfig sets
  // and which now reaches this file through src/worker/index.ts.
  return `${sanitizeSentryText(browserFrame[1] ?? '')}${stripSentryUrlSecrets(browserFrame[2] ?? '')}`
}

export function sanitizeSentryError(error: unknown, safeMessage = 'Operation failed'): Error {
  const sanitized = new Error(safeMessage)
  sanitized.name = sentryErrorType(error)

  if (error instanceof Error && typeof error.stack === 'string') {
    const frames = error.stack
      .split('\n')
      .slice(1)
      .map(sanitizeSentryStackFrame)
      .filter((frame): frame is string => frame !== null)
    sanitized.stack = [`${sanitized.name}: ${safeMessage}`, ...frames].join('\n')
  }

  return sanitized
}

function sanitizeSentryMetadataValue(value: unknown, key: string | undefined, seen: WeakSet<object>): unknown {
  if (key && isSensitiveMetadataKey(key)) {
    return SENTRY_REDACTED_VALUE
  }
  if (typeof value === 'string') return sanitizeSentryText(value)
  if (value === null || value === undefined || typeof value !== 'object') return value
  if (value instanceof Date) return value.toISOString()
  if (value instanceof Error) return sanitizeSentryError(value)
  if (seen.has(value)) return '[REDACTED-CIRCULAR]'

  seen.add(value)
  if (Array.isArray(value)) {
    const result = value.map((entry) => sanitizeSentryMetadataValue(entry, undefined, seen))
    seen.delete(value)
    return result
  }

  const result: Record<string, unknown> = {}
  for (const [entryKey, entryValue] of Object.entries(value)) {
    result[entryKey] = sanitizeSentryMetadataValue(entryValue, entryKey, seen)
  }
  seen.delete(value)
  return result
}

/** Clone arbitrary telemetry metadata through the one shared recursive redaction boundary. */
export function sanitizeSentryMetadata<T>(value: T): T {
  return sanitizeSentryMetadataValue(value, undefined, new WeakSet()) as T
}

/** Select only explicitly approved diagnostic fields, then recursively redact their values. */
export function allowSentryFields(
  value: Record<string, unknown> | null | undefined,
  allowedFields: readonly string[],
): Record<string, unknown> {
  if (!value) return {}
  const allowed = new Set(allowedFields)
  const result: Record<string, unknown> = {}
  for (const [key, entryValue] of Object.entries(value)) {
    if (allowed.has(key)) result[key] = sanitizeSentryMetadata(entryValue)
  }
  return result
}

/**
 * Path segments that ARE credentials: the contractor tax-form link
 * (`/tax-form/<token>` on the app, `/api/tax-forms/<token>[/upload]` on the
 * edge Worker). Unlike `/reset-password?token=…`, stripping the query leaves
 * these intact, so the segment itself is replaced.
 */
const TOKEN_PATH_PATTERN = /(\/(?:api\/)?tax-forms?\/)[^/?#\s)]+/gi

function redactTokenPaths(value: string): string {
  return value.replace(TOKEN_PATH_PATTERN, '$1[REDACTED]')
}

export function stripSentryUrlSecrets(value: string): string {
  try {
    const hasScheme = /^[a-z][a-z\d+.-]*:/i.test(value)
    const absolute = /^https?:\/\//i.test(value)
    if (hasScheme && !absolute) return '[REDACTED_URL]'
    const parsed = new URL(value, 'https://sentry.invalid')
    const pathname = redactTokenPaths(parsed.pathname)
    return absolute ? `${parsed.origin}${pathname}` : pathname
  } catch {
    return '[REDACTED_URL]'
  }
}

function sanitizeSentryRequest<T extends SanitizableSentryEvent>(event: T): T {
  if (!event.request) return event
  delete event.request.headers
  delete event.request.cookies
  delete event.request.query_string
  if (typeof event.request.url === 'string') {
    event.request.url = stripSentryUrlSecrets(event.request.url)
  }
  delete event.request.data
  return event
}

export function sanitizeSentryBreadcrumb<T extends SanitizableSentryBreadcrumb>(breadcrumb: T): T {
  if (typeof breadcrumb.message === 'string') {
    breadcrumb.message = sanitizeSentryText(breadcrumb.message)
  }
  if (!breadcrumb.data) return breadcrumb

  breadcrumb.data = sanitizeSentryMetadata(breadcrumb.data)

  for (const field of BREADCRUMB_URL_FIELDS) {
    if (typeof breadcrumb.data[field] === 'string') {
      breadcrumb.data[field] = stripSentryUrlSecrets(breadcrumb.data[field] as string)
    }
  }

  return breadcrumb
}

function sanitizeSentrySpanData(data: Record<string, unknown> | undefined): void {
  if (!data) return

  for (const key of Object.keys(data)) {
    const normalizedKey = key.toLowerCase()

    if (
      SENSITIVE_SPAN_ATTRIBUTE_KEYS.has(normalizedKey) ||
      HEADER_ATTRIBUTE_PREFIXES.some((prefix) => normalizedKey.startsWith(prefix))
    ) {
      delete data[key]
    } else if (
      (URL_ATTRIBUTE_FIELDS.has(normalizedKey) || normalizedKey.endsWith('.url')) &&
      typeof data[key] === 'string'
    ) {
      data[key] = stripSentryUrlSecrets(data[key] as string)
    } else if (typeof data[key] === 'string') {
      data[key] = sanitizeSentryText(data[key] as string)
    }
  }
}

export function sanitizeSentrySpan<T extends SanitizableSentrySpan>(span: T): T {
  if (typeof span.description === 'string') {
    span.description = sanitizeSentryTransactionName(span.description)
  }
  sanitizeSentrySpanData(span.data)
  return span
}

/** Transaction names arrive either bare (`/path?t=1`) or method-prefixed (`GET /path?t=1`). */
const HTTP_METHOD_PREFIX_PATTERN = /^(CONNECT|DELETE|GET|HEAD|OPTIONS|PATCH|POST|PUT|TRACE)\s+(.+)$/

function sanitizeSentryTransactionName(value: string): string {
  const match = HTTP_METHOD_PREFIX_PATTERN.exec(value)
  if (!match) return stripSentryUrlSecrets(value)
  // Same as above: both groups are mandatory, the `?? ''` is for the Worker
  // tsconfig's noUncheckedIndexedAccess only.
  return `${match[1] ?? ''} ${stripSentryUrlSecrets(match[2] ?? '')}`
}

export function sanitizeSentryEvent<T extends SanitizableSentryEvent>(event: T): T {
  sanitizeSentryRequest(event)
  if (typeof event.transaction === 'string') {
    event.transaction = sanitizeSentryTransactionName(event.transaction)
  }
  event.breadcrumbs?.forEach(sanitizeSentryBreadcrumb)
  if (event.user) {
    const stableId = event.user.id
    event.user = stableId === undefined || stableId === null ? {} : { id: stableId }
  }
  if (event.contexts) event.contexts = sanitizeSentryMetadata(event.contexts)
  if (event.extra) event.extra = sanitizeSentryMetadata(event.extra)
  if (typeof event.message === 'string') event.message = sanitizeSentryText(event.message)
  event.exception?.values?.forEach((exception) => {
    if (typeof exception.value === 'string') exception.value = sanitizeSentryText(exception.value)
  })
  return event
}

export function sanitizeSentryTransaction<T extends SanitizableSentryTransaction>(event: T): T {
  sanitizeSentryEvent(event)
  sanitizeSentrySpan(event.contexts?.trace ?? {})
  event.spans?.forEach(sanitizeSentrySpan)
  return event
}
